import DomainUser from '@/common/store/modules/domains/createDomainUser';
import { resourceFunctor } from '@/common/store/modules/common/common.actions';
import createCommonModule from '@/common/store/modules/common';
import { STATE_ICON_DISABLED, STATE_ICON_ENABLED } from '@/common/utils/statuses';
import {
  DOMAIN_USER_DOMAIN_ADMIN_ROLE,
  DOMAIN_USER_PROJECT_MEMBER_ROLE,
  DOMAIN_USER_SERVICE_ACCOUNT_ROLE,
  DOMAIN_USER_SYSTEM_ADMIN_ROLE,
  PROJECT_NONE_USER_ROLE,
} from '@/common/constants/domains.constants';
import i18n from '@/common/i18n';
import Vue from 'vue';
import { pick } from 'lodash-es';

const DOMAIN_USER_STATUSES_ICONS_MAP = {
  enabled: STATE_ICON_ENABLED,
  disabled: STATE_ICON_DISABLED,
};

const getDomainId = ({ rootState, rootGetters }) => rootState?.routes?.params?.domainId || rootGetters.currentDomainId;

const domainUsersModule = {
  Model: DomainUser,
  pagingSupport: true,
  resource: store => Promise.resolve(`/api/v2/domains/${getDomainId(store)}/users`),
  state: {
    assignedGroups: {}
  },
  getters: {
    statusText: () => ({
      enabled: i18n.t('Enabled'),
      disabled: i18n.t('Disabled'),
    }),

    _mapItem: (state, getters) => id => {
      const user = state.itemsMap[id];
      if (!user) {
        return null;
      }
      const status = user.isEnabled ? 'enabled' : 'disabled';

      return {
        ...user,
        status,
        statusText: getters.statusText[status],
        statusIcon: DOMAIN_USER_STATUSES_ICONS_MAP[status],
        isDomainAdmin: user.role === DOMAIN_USER_DOMAIN_ADMIN_ROLE,
        isServiceUser: user.role === DOMAIN_USER_SERVICE_ACCOUNT_ROLE,
        isSystemAdmin: user.role === DOMAIN_USER_SYSTEM_ADMIN_ROLE,
        isProjectMember: user.role === DOMAIN_USER_PROJECT_MEMBER_ROLE,
        assignedProjects: user.assignedProjects?.map(r => ({
          projectId: r.project_id,
          role: r.role
        })) || [],
      };
    },

    getUserProjectsById: (state, getters, rootState) => (userId) => {
      const userProjectRoles = getters.getItemById(userId)?.assignedProjects || [];

      return userProjectRoles?.map((projectRole) => {
        const project = rootState.domains.projects.itemsMap[projectRole.projectId];

        return {
          ...project,
          ...projectRole,
        };
      });
    },
    getAssignedGroups: state => userId => state.assignedGroups[userId],
  },
  mutations: {
    SET_ASSIGNED_GROUPS(state, { userId, data = [] }) {
      Vue.set(state.assignedGroups, userId, data.map(group => pick(group, ['id', 'name', 'description'])));
    },
  },
  actions: ({ resource, Model }) => ({
    FETCH_ITEM(store, { id, params = {}, domainId, force = false, merge = true, options = {} }) {
      const { commit } = store;

      // FETCH may happen on socket event when user is created
      // there is no need to FETCH if we are not working with specific domain
      if (getDomainId(store) !== domainId && !force) {
        return {};
      }

      return resourceFunctor(resource, store)
        .then(resourceStr => this.$api.get(`${resourceStr}/${id}`, params, options))
        .then((res) => {
          const item = new Model(res.data);
          commit('CHANGE', { item, merge });
          return res;
        })
        .catch((error) => {
          commit('SET_ERRORS', error);

          throw error;
        });
    },
    ASSIGN_PROJECT_ROLES({ dispatch }, { userId, projects }) {
      return dispatch('UPDATE', {
        id: userId,
        body: {
          assigned_projects: projects.map(project => ({
            project_id: project.projectId,
            role: project.role
          }))
        }
      });
    },
    REMOVE_PROJECT_ROLES({ dispatch }, { userId, projectIds }) {
      return dispatch('UPDATE', {
        id: userId,
        body: {
          assigned_projects: projectIds.map(projectId => ({
            project_id: projectId,
            role: PROJECT_NONE_USER_ROLE
          }))
        }
      });
    },
    DELETE({ dispatch }, { item }) {
      return dispatch('DELETE_ITEMS', { items: [item] });
    },
    DELETE_ITEMS(store, { items }) {
      const { commit } = store;

      return resourceFunctor(resource, store)
        .then(resourceStr => this.$api.delete(resourceStr, { uuids: items.map(it => it.id) }))
        .then((res) => {
          commit('CLEAR_ERRORS');
          items.forEach(it => commit('REMOVE', it.id));
          return res;
        })
        .catch((error) => {
          commit('SET_ERRORS', error);
          throw error;
        });
    },
    UPDATE_ASSIGNED_PROJECTS({ dispatch }, { userId, projectsToAssign = [], projectIdsToUnassign = [], domainPermissions }) {
      return dispatch('UPDATE', {
        id: userId,
        body: {
          assigned_projects: [
            ...projectsToAssign.map(({ id, role }) => ({ project_id: id, role })),
            ...projectIdsToUnassign.map(id => ({ project_id: id, role: PROJECT_NONE_USER_ROLE }))
          ],
          ...(domainPermissions ? { domain_permissions: domainPermissions } : {}),
        }
      });
    },
    FETCH_ASSIGNED_GROUPS({ commit }, { domainId, userId }) {
      return this.$api.get(`/api/v2/domains/${domainId}/users/${userId}/groups/`)
        .then(({ data }) => {
          commit('SET_ASSIGNED_GROUPS', {
            userId,
            data: data.data
          });
        });
    },
  })
};
export { domainUsersModule };
export default createCommonModule(domainUsersModule);
